import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';

import {
  UntypedFormGroup,
  UntypedFormBuilder,
  Validators,
} from '@angular/forms';
import { BsDatepickerConfig, BsLocaleService } from 'ngx-bootstrap/datepicker';
import { BsModalRef } from 'ngx-bootstrap/modal';

import { RefDataService } from '@app/providers/ref-data/ref-data.service';
import { LoansService } from '@app/providers/loans/loans.service';
import { UserService } from '@app/providers/user/user.service';

import {
  NotificationsService,
  NotificationType,
} from '@app/components/notification/NotificationsService';
import * as _ from 'lodash';

import { debounceTime, distinctUntilChanged, map, switchMap } from 'rxjs';

@Component({
    selector: 'ea-loan-form',
    templateUrl: './loan-form.component.html',
    styleUrls: ['./loan-form.component.scss'],
    standalone: false
})
export class LoanFormComponent implements OnInit {
  @Input() userId: any;
  @Input() loan: any;
  @Input() isEstate: any;
  @Output() update = new EventEmitter();
  @Output() delete = new EventEmitter();
  @Output() dissociate = new EventEmitter();

  modalRef: BsModalRef;
  loading = false;
  submitting = false;

  loanPercentage = 0;
  loanForm: UntypedFormGroup;
  typeProjetList: any;
  typeRateList: any;
  startDate: any;
  signatureDate: any;

  bsConfig: Partial<BsDatepickerConfig>;
  datepickerLocale = 'fr';

  constructor(
    private fb: UntypedFormBuilder,
    public refDataService: RefDataService,
    private loansService: LoansService,
    private notification: NotificationsService,
    private userService: UserService,
    private _localeService: BsLocaleService,
  ) {
    this._localeService.use(this.datepickerLocale);
    this.bsConfig = Object.assign(
      {},
      { locale: this.datepickerLocale, containerClass: 'theme-blue' },
    );
  }

  ngOnInit() {
    this.refDataService.getLoanTypeProjet().subscribe((res: any) => {
      this.refDataService.loanTypeProjet = res.data;
      this.typeProjetList = res.data;
    });

    this.refDataService.getLoanTypeRate().subscribe(res => {
      this.refDataService.loanTypeRate = res.data;
      this.typeRateList = res.data;
    });

    this.buildLoanForm(this.loan);
  }

  buildLoanForm(loan: any) {
    this.loanForm = this.fb.group({
      metadata: this.fb.group({
        id: [null, Validators.compose([Validators.required])],
        label: '',
        typeProject: [null, Validators.compose([Validators.required])],
        typeRate: [null, Validators.compose([Validators.required])],
      }),
      data: this.fb.group({
        amount: [null, Validators.compose([Validators.required])],
        duration: [null, Validators.compose([Validators.required])],
        monthAmount: '',
        rate: [null, Validators.compose([Validators.required])],
        insuranceRate: [null, Validators.compose([Validators.required])],
      }),
    });

    this.startDate = new Date(loan.startDate);
    this.signatureDate = new Date(loan.signatureDate);

    this.loanForm.get('metadata.id').setValue(loan.id);
    this.loanForm.get('metadata.label').setValue(loan.label);
    this.loanForm.get('metadata.typeProject').setValue(loan.typeProject);
    this.loanForm.get('metadata.typeRate').setValue(loan.typeRate);

    let rate =
      typeof loan.rate === 'number' ? (loan.rate * 100).toFixed(2) : loan.rate;
    let insuranceRate =
      typeof loan.insuranceRate === 'number'
        ? (loan.insuranceRate * 100).toFixed(2)
        : loan.insuranceRate;
    this.loanForm
      .get('data.monthAmount')
      .setValue(this.round(loan.monthAmount));
    this.loanForm.get('data.amount').setValue(loan.amount);
    this.loanForm.get('data.duration').setValue(loan.duration);
    this.loanForm.get('data.rate').setValue(rate);
    this.loanForm.get('data.insuranceRate').setValue(insuranceRate);

    this.loanForm
      .get('data')
      .valueChanges.pipe(
        debounceTime(1000),
        map(data => _.omit(data, 'monthAmount')),
        distinctUntilChanged(_.isEqual),
      )
      .subscribe(body => this.updateMonthlyAmount(body));
  }

  private updateMonthlyAmount(body: any) {
    const amount = body.amount;
    const rate = body.rate;
    const insuranceRate = body.insuranceRate;
    const duration = body.duration;

    if (amount && duration && rate && insuranceRate) {
      this.loansService
        .computeLoanMonthlyAmount(
          amount,
          duration,
          Number(rate) / 100,
          Number(insuranceRate) / 100,
        )
        .subscribe(value => this.setMonthlyAmount(this.round(value)));
    } else {
      this.setMonthlyAmount('');
    }
  }

  private setMonthlyAmount(value: any) {
    this.loanForm.get('data.monthAmount').setValue(value, {
      onlySelf: true,
      emitEvent: false,
    });
  }

  private round(input: number): number {
    if (input) {
      return +input.toFixed(2);
    } else {
      return null;
    }
  }

  onSubmit(formValue: any) {
    if (!this.submitting) {
      this.submitting = true;

      const body: any = {
        id: formValue.metadata.id,
        label: formValue.metadata.label,
        typeProject: formValue.metadata.typeProject,
        typeRate: formValue.metadata.typeRate,
        amount: formValue.data.amount,
        duration: formValue.data.duration,
        rate: Number(formValue.data.rate) / 100,
        insuranceRate: Number(formValue.data.insuranceRate) / 100,
      };

      if (this.startDate) {
        body.startDate = this.startDate.getTime();
      }
      if (this.signatureDate) {
        body.signatureDate = this.signatureDate.getTime();
      }

      this.userService
        .authOnce$()
        .pipe(switchMap(user => this.loansService.updateLoan(user.id, body)))
        .subscribe({
          next: (res: any) => {
            this.loading = false;
            let htmlElms =
              '<i class="fa fa-check"></i> La mise à jour a été effectuée avec succès !';
            this.notification.html(htmlElms, NotificationType.Success);
            this.update.emit({ edit: true });
          },
          error: (err: any) => {
            this.loading = false;
            let htmlElms =
              '<i class="fa fa-exclamation-triangle"></i> Oups ! La mise à jour a échouée.';
            this.notification.html(htmlElms, NotificationType.Error);
          },
        });
    }
  }

  onDissociate(): void {
    if (this.isEstate) {
      this.dissociate.emit(this.loan);
    }
  }

  onDeleteConfirm(data: any) {
    this.loading = true;
    this.userService
      .authOnce$()
      .pipe(
        switchMap(user => this.loansService.deleteLoan(user.id, this.loan.id)),
      )
      .subscribe({
        next: (res: any) => {
          let htmlElms =
            '<i class="fa fa-check"></i> La suppression a été effectuée avec succès !';
          this.notification.html(htmlElms, NotificationType.Success);
          this.delete.emit({ delete: true });
          this.loading = false;
        },
        error: err => (this.loading = false),
      });
  }
}
